import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { doc as fsDoc, getDoc } from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { db } from "../Firebase/firebase";
import { useAuth } from "../contexts/AuthContext";
import RateRecipeDialog from "./RateRecipeDialog";
import Bookmark from "../../widgets/Bookmarks";
import { Button, Result, message } from "antd";
import { InfoSharp, SaveAlt } from "@material-ui/icons";
import { pdf } from "@react-pdf/renderer";
import PostDocument from "../../widgets/PdfPostCreator";
import ShareOnTwitter from "../../widgets/ShareOnTwitter";
import ShareOnFacebook from "../../widgets/ShareOnFacebook";

const isBrowser = typeof window !== "undefined";

// Escape JSON-LD payloads so a stray "</script>" in recipe content (titles,
// ingredients, instructions) can't break out of the <script> tag. Mirrors the
// SSR-side escape in functions/index.js so client-rendered routes keep
// Google Rich Results validity.
const safeJsonForScript = (value) =>
  JSON.stringify(value).replace(/</g, "\\u003c");

// ---------------------------------------------------------- A single post page (SSR-aware) -------------------------------------------------
export default function Post() {
  const { category, recipe } = useParams(); // route: /post/:category/:recipe
  const recipeId = recipe;
  const { currentUser } = useAuth();

  const [postInformation, setPostInformation] = useState(null); // { id, data: () => {...} }
  const [loading, setLoading] = useState(true);
  const [isPrivateLock, setPrivateLock] = useState(false);
  const [allowComments, setAllowComments] = useState(false);
  const [url, setUrl] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // ---------- Prefer SSR data if present ----------
  useEffect(() => {
    let hydrated = false;
    if (isBrowser && window.__INITIAL_DATA__ && window.__INITIAL_DATA__.post) {
      const p = window.__INITIAL_DATA__.post;
      if (p.id === recipeId) {
        setAllowComments(Boolean(p.allowComments));
        if (p.privatePosts && p.id_of_user !== currentUser?.uid) {
          setPostInformation([]);
          setPrivateLock(true);
        } else {
          setPostInformation({ id: p.id, data: () => p });
          setPrivateLock(false);
        }
        setLoading(false);
        hydrated = true;
      }
    }
    if (hydrated) return;

    // ---------- Client fetch fallback (direct doc fetch; no collection scan) ----------
    (async () => {
      try {
        const snap = await getDoc(fsDoc(db, "posts", recipeId));
        if (!snap.exists()) {
          setPrivateLock(true);
          setPostInformation([]);
          setAllowComments(false);
          setLoading(false);
          return;
        }
        const p = snap.data();
        setAllowComments(Boolean(p.allowComments));
        if (p.privatePosts && p.id_of_user !== currentUser?.uid) {
          setPrivateLock(true);
          setPostInformation([]);
        } else {
          setPrivateLock(false);
          setPostInformation({ id: snap.id, data: () => p });
        }
      } catch (e) {
        console.error("Fetch post error:", e);
        setPrivateLock(true);
        setPostInformation([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [recipeId, currentUser?.uid]);

  // SEO-friendly data extraction
  const getPostData = () => {
    if (!postInformation || typeof postInformation.data !== "function")
      return null;
    return postInformation.data();
  };
  const postData = getPostData();

  // ====== Base constants reused by SEO ======
  const siteBase = "https://gentlegourmet.app";
  const siteName = "GentleGourmet";
  const brand = "GentleGourmet";
  const locale = "en_US";
  const canonicalUrl = `${siteBase}/post/${encodeURIComponent(
    category || ""
  )}/${encodeURIComponent(recipeId || "")}`;
  const ogImage =
    postData?.imageUrl || `${siteBase}/images/gentlegourmeticon.png`;
  const toISO8601 = (value) => {
    try {
      if (!value) return null;

      // Firestore Timestamp
      if (typeof value?.toDate === "function")
        return value.toDate().toISOString();

      // JS Date
      if (value instanceof Date) return value.toISOString();

      // number (epoch ms)
      if (typeof value === "number") return new Date(value).toISOString();

      // string
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toISOString();

      return null;
    } catch {
      return null;
    }
  };

  const caloriesToText = (cals) => {
    if (cals === null || cals === undefined || cals === "") return null;
    if (typeof cals === "number") return `${Math.round(cals)} calories`;
    // if already a string, make sure it contains "cal"
    const s = String(cals).trim();
    return /cal/i.test(s) ? s : `${s} calories`;
  };

  // Generate SEO metadata
  const generateSEOData = (pd) => {
    if (!pd) return {};
    const seoTitle = `${pd.title} - ${pd.category} Recipe | GentleGourmet`;
    const descriptionBase =
      (pd.desc || "").trim() ||
      `Digestive-friendly ${pd.category || "healthy"} recipe from GentleGourmet.`;
    const descriptionWithAuthor = `${descriptionBase} Easy ${
      pd.category || "digestive-friendly"
    } recipe by ${pd.username_of_user || "GentleGourmet"}.`;
    const metaDescription =
      descriptionWithAuthor.length > 158
        ? `${descriptionWithAuthor.substring(0, 155).trimEnd()}...`
        : descriptionWithAuthor;
    const keywords = [
      pd.category?.toLowerCase(),
      "recipe",
      "digestive friendly",
      "healthy cooking",
      pd.title?.toLowerCase(),
      ...(pd.ingredients
        ?.slice(0, 5)
        .map((ing) => (ing.label || "").toLowerCase()) || []),
    ]
      .filter(Boolean)
      .join(", ");
    return {
      title: seoTitle,
      description: metaDescription,
      keywords,
      url: canonicalUrl,
      image: pd.imageUrl,
      author: pd.username_of_user,
      publishedTime: toISO8601(pd.createdAt) || new Date().toISOString(),
      modifiedTime:
        toISO8601(pd.updatedAt) ||
        toISO8601(pd.createdAt) ||
        new Date().toISOString(),
    };
  };
  const seoData = generateSEOData(postData);

  // Calculate cooking/prep time
  const getCookingTime = (instructions) => {
    if (!instructions) return 30;
    return Math.max(15, instructions.length * 5);
  };
  const getServings = () => 4;

  const CUISINE_TAG_PATTERN =
    /(african|american|asian|european|indian|creole|mediterranean|latin|caribbean|middle eastern|french|italian|mexican|chinese|japanese|korean|thai|vietnamese)/i;
  const getRecipeCuisine = (pd) => {
    const tag = (pd?.tags || []).find(
      (t) => typeof t === "string" && CUISINE_TAG_PATTERN.test(t)
    );
    return tag || "International";
  };

  // Save Post information as pdf file
  const SaveButton = ({ postInformation }) => {
    const handleClick = async () => {
      setSaveLoading(true);
      if (postInformation && typeof postInformation.data === "function") {
        const data = postInformation.data();
        if (data?.imageUrl) {
          try {
            const pdfDoc = (
              <PostDocument
                title={data?.title}
                description={data?.desc}
                ingredients={data?.ingredients}
                instructions={data?.instructionsForRecipe}
                email={(data?.email || "").slice(
                  0,
                  (data?.email || "").lastIndexOf("@")
                )}
              />
            );
            const blob = await pdf(pdfDoc).toBlob();
            const fileURL = URL.createObjectURL(blob);
            setUrl(fileURL);
          } catch (error) {
            message.error("Error in saving the post");
          } finally {
            setSaveLoading(false);
          }
        } else {
          setSaveLoading(false);
          message.error("Error in saving the post");
        }
      } else {
        setSaveLoading(false);
        message.error("Error in saving the post");
      }
    };

    useEffect(() => {
      if (!isBrowser) return;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${postInformation?.data()?.title}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        setUrl(null);
      }
    }, [url, postInformation]);

    return (
      <Button
        type="secondary"
        icon={<SaveAlt />}
        onClick={handleClick}
        loading={saveLoading}
        aria-label="Download recipe as PDF"
        className="h-12 w-12 flex items-center justify-center rounded-full bg-white border border-gray-300 hover:bg-gray-50"
      />
    );
  };

  // ✅ If you keep react-snap, guard for SSR
  useEffect(() => {
    if (!isBrowser) return;
    if (!loading && postInformation?.data) {
      window.snapSaveState = () => ({
        recipeId,
        postData: postInformation.data(),
      });
    }
  }, [loading, recipeId, postInformation]);

  // JSON-LD structured data
  const jsonLdWebsite = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: siteName,
      description:
        "Discover delicious, digestive-friendly recipes for GERD, IBS, gastritis, and Barrett's esophagus. Search meals by symptoms, ingredients, and tolerance. Evidence-informed cooking tips for calmer, happier digestion.",
      url: siteBase + "/",
      potentialAction: {
        "@type": "SearchAction",
        target: `${siteBase}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
      publisher: {
        "@type": "Organization",
        name: brand,
        url: siteBase + "/",
        logo: { "@type": "ImageObject", url: `${siteBase}/images/GG.png` },
      },
    }),
    []
  );

  const jsonLdOrganization = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand,
      url: siteBase + "/",
      logo: `${siteBase}/images/GG.png`,
    }),
    []
  );

  const jsonLdBreadcrumbs = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteBase + "/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Recipes",
          item: `${siteBase}/explore`,
        },
        ...(postData?.title
          ? [
              {
                "@type": "ListItem",
                position: 3,
                name: postData.title,
                item: canonicalUrl,
              },
            ]
          : []),
      ],
    }),
    [postData?.title, canonicalUrl]
  );

  const faqItems = [
    {
      q: "Are these recipes medical advice?",
      a: "No. GentleGourmet shares general, educational content. Always consult your healthcare professional for personalized guidance.",
    },
    {
      q: "Can I search by ingredients and triggers?",
      a: "Yes. Use the search box to filter by ingredients, cuisines, or terms like 'low FODMAP' or 'dairy-free.'",
    },
    {
      q: "Do you support GERD, IBS, gastritis, and Barrett's esophagus?",
      a: "Yes. We curate categories for each to help you discover options that may be gentler on digestion.",
    },
  ];

  const jsonLdFAQ = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
    []
  );

  const recipeJsonLd = useMemo(() => {
    if (!postData) return null;

    const publishedISO =
      toISO8601(postData.createdAt) || new Date().toISOString();

    const modifiedISO =
      toISO8601(postData.updatedAt) ||
      toISO8601(postData.createdAt) ||
      new Date().toISOString();

    const description =
      (postData.desc && postData.desc.trim()) ||
      `A digestive-friendly ${
        postData.category || ""
      } recipe from GentleGourmet.`;

    const keywords =
      (seoData.keywords && seoData.keywords.trim()) ||
      [
        postData.category,
        "GERD friendly",
        "reflux friendly",
        "low acid",
        "recipe",
      ]
        .filter(Boolean)
        .join(", ");

    const caloriesText =
      caloriesToText(postData.totalCalories) || "200 calories";

    const authorName =
      (postData.username_of_user && postData.username_of_user.trim()) || null;

    return {
      "@context": "https://schema.org",
      "@type": "Recipe",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl,
      },
      name: postData.title,
      image: postData?.imageUrl ? [postData.imageUrl] : undefined,

      // safest: always valid object type
      author: authorName
        ? { "@type": "Person", name: authorName }
        : { "@type": "Organization", name: brand, url: siteBase },

      datePublished: publishedISO,
      dateModified: modifiedISO,

      description,
      keywords,

      recipeCategory: postData.category,
      recipeCuisine: getRecipeCuisine(postData),

      recipeYield: `${getServings()} servings`,
      prepTime: "PT15M",
      cookTime: `PT${getCookingTime(postData.instructionsForRecipe)}M`,
      totalTime: `PT${getCookingTime(postData.instructionsForRecipe) + 15}M`,

      nutrition: {
        "@type": "NutritionInformation",
        calories: caloriesText,
      },

      recipeIngredient:
        postData.ingredients?.map((i) => i?.label).filter(Boolean) || [],

      recipeInstructions: (() => {
        const steps = (postData.instructionsForRecipe || [])
          .map((step, index) => {
            const text =
              (step && typeof step === "object" ? step.text : step) ||
              String(step || "");
            return text && text.trim()
              ? {
                  "@type": "HowToStep",
                  name: `Step ${index + 1}`,
                  text: text.trim(),
                  position: index + 1,
                }
              : null;
          })
          .filter(Boolean);
        return steps.length
          ? steps
          : [
              {
                "@type": "HowToStep",
                name: "Step 1",
                text: `Prepare ${
                  postData.title || "this recipe"
                } following the ingredient list above.`,
                position: 1,
              },
            ];
      })(),

      aggregateRating:
        postData.rating && postData.ratingCount
          ? {
              "@type": "AggregateRating",
              ratingValue: Number(postData.rating),
              ratingCount: Number(postData.ratingCount),
              bestRating: 5,
              worstRating: 1,
            }
          : undefined,

      publisher: {
        "@type": "Organization",
        name: brand,
        url: siteBase,
        logo: { "@type": "ImageObject", url: `${siteBase}/images/GG.png` },
      },
      inLanguage: "en-US",
    };
  }, [postData, seoData.keywords, canonicalUrl, siteBase, brand]);

  const recipeWebPageJsonLd = useMemo(() => {
    if (!postData) return null;
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: seoData.title,
      description: seoData.description,
      url: canonicalUrl,
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: siteName,
        url: `${siteBase}/`,
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: ogImage,
      },
    };
  }, [postData, seoData.title, seoData.description, canonicalUrl, siteName, siteBase, ogImage]);

  // Loading skeleton
  const PostSkeleton = () => (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
      <div className="animate-pulse">
        <div className="bg-gray-300 h-8 rounded mb-4 w-3/4"></div>
        <div className="bg-gray-300 h-4 rounded mb-8 w-1/2"></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-300 h-16 rounded-lg"></div>
          ))}
        </div>
        <div className="bg-gray-300 h-64 sm:h-80 rounded-xl mb-8"></div>
        <div className="bg-gray-300 h-6 rounded mb-4 w-1/3"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-gray-300 h-12 rounded-lg mb-2"></div>
        ))}
        <div className="bg-gray-300 h-6 rounded mt-8 mb-4 w-1/3"></div>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-300 h-16 rounded-lg mb-3"></div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* SEO Head Tags */}
      <Helmet>
        <html lang="en" />
        {!postData && !loading && (
          <>
            <title>Recipe not found | GentleGourmet</title>
            <meta
              name="description"
              content="The recipe you are looking for is private, unavailable, or has been removed."
            />
            <meta name="robots" content="noindex,follow" />
          </>
        )}

        {postData && (
          <>
            {/* Primary */}
            <title>{seoData.title}</title>
            <meta name="description" content={seoData.description} />
            <meta name="keywords" content={seoData.keywords} />
            <link rel="canonical" href={seoData.url} />
            <meta
              name="robots"
              content="index,follow,max-image-preview:large"
            />
            <meta name="author" content={brand} />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />

            {/* Open Graph */}
            <meta property="og:title" content={seoData.title} />
            <meta property="og:description" content={seoData.description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:url" content={seoData.url} />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content={locale} />
            <meta
              property="og:image:alt"
              content={`${postData.title} digestive-friendly recipe`}
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seoData.title} />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:site" content="@gentlegourmetapp" />
            <meta name="application-name" content={siteName} />
            <meta name="apple-mobile-web-app-title" content={siteName} />
            <link rel="alternate" hrefLang="en-us" href={canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

            {/* Article */}
            <meta property="article:author" content={seoData.author || brand} />
            <meta
              property="article:published_time"
              content={seoData.publishedTime}
            />
            <meta
              property="article:modified_time"
              content={seoData.modifiedTime}
            />
            <meta property="article:section" content="Recipes" />
            <meta
              property="article:tag"
              content={postData.category || "Recipes"}
            />

            {/* JSON-LD: put Recipe FIRST, then the site-wide ones */}
            {recipeJsonLd && (
              <script type="application/ld+json">
                {safeJsonForScript(recipeJsonLd)}
              </script>
            )}
            {recipeWebPageJsonLd && (
              <script type="application/ld+json">
                {safeJsonForScript(recipeWebPageJsonLd)}
              </script>
            )}

            <script type="application/ld+json">
              {safeJsonForScript(jsonLdWebsite)}
            </script>

            <script type="application/ld+json">
              {safeJsonForScript(jsonLdOrganization)}
            </script>

            {jsonLdBreadcrumbs && (
              <script type="application/ld+json">
                {safeJsonForScript(jsonLdBreadcrumbs)}
              </script>
            )}

            <script type="application/ld+json">
              {safeJsonForScript(jsonLdFAQ)}
            </script>
          </>
        )}
      </Helmet>

      {loading ? (
        <PostSkeleton />
      ) : isPrivateLock ? (
        <main className="min-h-screen bg-gray-50 pt-24 pb-8">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <Result
                status="403"
                title="This post is private or no longer exists"
                subTitle="The recipe you're looking for is either private or has been removed."
                extra={
                  <Button type="primary" href="/explore">
                    Browse Recipes
                  </Button>
                }
              />
            </div>
          </div>
        </main>
      ) : postInformation ? (
        <main className="min-h-screen bg-gray-50 pt-24 pb-8">
          <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Recipe Header */}
            <div className="p-6 sm:p-8 border-b border-gray-100">
              <div className="mb-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                  {postData?.title}
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed">
                  {postData?.desc}
                </p>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>Recipe by</span>
                  <Link
                    reloadDocument
                    to={`/profile/${postData?.username_of_user}`}
                    className="font-medium text-green-600 hover:text-green-700 transition-colors"
                  >
                    {postData?.username_of_user}
                  </Link>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  {currentUser && (
                    <Bookmark
                      postID={postInformation?.id}
                      userID={currentUser?.uid}
                    />
                  )}
                  <SaveButton postInformation={postInformation} />

                  {/* Share Buttons (guard for SSR) */}
                  <div className="flex items-center gap-1 ml-2">
                    {isBrowser && (
                      <>
                        <ShareOnTwitter
                          url={window.location.href}
                          text={`Check out "${postData?.title}" - a delicious ${postData?.category} recipe from GentleGourmet`}
                        />
                        <ShareOnFacebook
                          url={window.location.href}
                          quote={`Check out "${postData?.title}" on GentleGourmet`}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recipe Image */}
            <figure className="w-full">
              <img
                src={
                  postData?.imageUrl ||
                  "https://firebasestorage.googleapis.com/v0/b/gingerrmarket.appspot.com/o/defaultImages%2Fphoto-1546069901-ba9599a7e63c.jpg?alt=media&token=c55c094f-91f0-4d7a-85d4-8ae2d125cce2"
                }
                alt={`${postData?.title} - ${postData?.category} recipe`}
                width="800"
                height="450"
                loading="lazy"
                className="w-full h-64 sm:h-80 object-cover"
              />
              <figcaption className="sr-only">
                {postData?.title} recipe photo
              </figcaption>
            </figure>

            {/* Recipe Meta Grid */}
            <div className="p-6 sm:p-8 border-b border-gray-100">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600">15</div>
                  <div className="text-sm text-gray-600 mt-1">Prep Time</div>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-bold text-green-600">
                    {getCookingTime(postData?.instructionsForRecipe)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Cook Time</div>
                  <meta
                    content={`PT${getCookingTime(
                      postData?.instructionsForRecipe
                    )}M`}
                  />
                </div>

                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <div className="text-2xl font-bold text-orange-600">
                    {getServings()}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Servings</div>
                  <meta content={getServings().toString()} />
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <div className="text-lg font-bold text-purple-600 capitalize">
                    {postData?.category}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Category</div>
                  <meta content={postData?.category} />
                </div>
              </div>
            </div>

            {/* Ingredients Section */}
            <section className="p-6 sm:p-8 border-b border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2 h-6 bg-green-500 rounded-full mr-3"></span>
                Ingredients
              </h2>

              <div className="space-y-3">
                {postData?.ingredients?.map((ingredient, index) => (
                  <div
                    key={index}
                    className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <FormGroup className="w-full">
                      <FormControlLabel
                        control={
                          <Checkbox
                            className="transform scale-125"
                            sx={{
                              color: "#10B981",
                              "&.Mui-checked": {
                                color: "#10B981",
                              },
                            }}
                          />
                        }
                        label={
                          <span className="text-gray-700 text-base">
                            <span className="font-medium capitalize">
                              {(ingredient?.label || "").toLowerCase()}
                            </span>
                            {ingredient?.calories && (
                              <span className="text-gray-500 text-sm ml-2">
                                ({ingredient.calories} cal)
                              </span>
                            )}
                          </span>
                        }
                        className="w-full m-0"
                      />
                    </FormGroup>
                  </div>
                ))}
              </div>
            </section>

            {/* Directions Section */}
            <section className="p-6 sm:p-8 border-b border-gray-100">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2 h-6 bg-blue-500 rounded-full mr-3"></span>
                Directions
              </h2>

              <div className="space-y-4">
                {postData?.instructionsForRecipe?.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-green-200 transition-colors"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold mt-1">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Step {index + 1}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {step.text}
                      </p>
                      <meta content={String(index + 1)} />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Rating Section */}
            <div className="p-6 sm:p-8">
              <RateRecipeDialog
                title={postData?.title}
                GETPOSTSLUG={postInformation.id}
              />
            </div>

            {/* Comments Notice */}
            {!allowComments && (
              <div className="p-6 sm:p-8 bg-amber-50 border-t border-amber-200">
                <div className="flex items-start gap-3">
                  <InfoSharp className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-amber-800">
                      Comments Disabled
                    </h3>
                    <p className="text-amber-700 text-sm mt-1">
                      The author has disabled comments for this recipe. However,
                      you can still rate it above.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </article>
        </main>
      ) : null}
    </>
  );
}
